import {
  DataEntryOption,
  DetailRequirement,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  PracticeDetailFields,
  SingularTaskTraitDetail,
} from '@cibo/core'
import { usePracticesContent } from '@cibo/landmanager'
import { FriendlyError } from '@cibo/ui'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { alpha, Button, Grid2, Paper, Stack, Typography, useTheme } from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePracticeDrawer } from '../../../../components/PracticeDrawer'
import { TaskDataModel } from '../../models/TaskDataModel'

type DataEntryTilesSelectorProps = {
  options: DataEntryOption[]
  detailRequirement?: DetailRequirement
  traitConfig: EngagementTaskTrait
  taskData: UseQueryResult<TaskDataModel, Error>
  traitDetailValue: any
  handleChangeDetail: (detail: MultipleTaskTraitDetail | SingularTaskTraitDetail) => void
  immutable?: boolean
  removeText?: string
  selectText?: string
}

type CombinedOption = DataEntryOption & {
  description?: string | null
  contentFields?: PracticeDetailFields
}

export const DataEntryTilesSelector = ({
  options,
  detailRequirement,
  traitConfig,
  taskData,
  traitDetailValue,
  handleChangeDetail,
  immutable,
  removeText,
  selectText,
}: DataEntryTilesSelectorProps) => {
  const { userDetails, wfDetails } = taskData.data || {}
  const { t } = useTranslation('@cibo/programs/DataEntryTilesSelector')
  const theme = useTheme()
  const { setDrawerState, drawerState } = usePracticeDrawer()
  const practiceContent = usePracticesContent()
  const [selectedTileValues, setSelectedTileValues] = useState<string[]>([])

  const combinedOptions: CombinedOption[] = useMemo(() => {
    if (practiceContent.data) {
      return options.map(option => {
        const practice = practiceContent.data.items.find(
          p => (p.fields.practiceId as any as string) === option.value
        )
        return {
          description: practice ? practice.fields.definition : null,
          contentFields: practice ? practice?.fields : null,
          ...option,
        }
      })
    } else {
      return options
    }
  }, [options, practiceContent.dataUpdatedAt])

  useEffect(() => {
    if (!selectedTileValues.length) {
      if (traitDetailValue) {
        setSelectedTileValues(traitDetailValue as string[])
      }
    }
  }, [userDetails, wfDetails, traitConfig.traitId, traitConfig.dataType, selectedTileValues.length])

  const handleSaveTraitDetail = (option: DataEntryOption) => {
    setSelectedTileValues(prev => {
      const previousValue = typeof prev === 'string' ? [prev] : prev
      const isRemoving = prev.includes(option.value)
      const values = option.exclusive
        ? [option.value]
        : isRemoving
        ? previousValue.filter(valueItem => valueItem !== option.value)
        : [...previousValue, option.value].filter(
            valueItem => !options.find(option => option.value === valueItem)?.exclusive
          )
      const updateValue = {
        traitId: traitConfig.traitId,
        value: values,
        year: detailRequirement?.year,
      }

      handleChangeDetail(updateValue)

      return values
    })
  }

  if (!taskData) {
    return <FriendlyError message="Task data is not available." />
  }

  return (
    <Grid2 container spacing={2}>
      {combinedOptions.map(option => {
        const isSelected = selectedTileValues.includes(option.value)
        return (
          <Grid2 size={6} key={option.label}>
            <Paper
              variant="outlined"
              sx={{
                borderColor: isSelected ? theme.palette.secondary.light : undefined,
                backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.1) : undefined,
              }}
            >
              <Stack spacing={1} padding={2}>
                <Typography variant="body1" sx={{ fontWeight: '500' }}>
                  {option.label}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {option.description || null}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    color={isSelected ? 'primary' : 'secondary'}
                    onClick={() => handleSaveTraitDetail(option)}
                    disabled={immutable}
                    startIcon={isSelected ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                  >
                    {isSelected ? removeText || t('unselectGoal') : selectText || t('selectGoal')}
                  </Button>
                  {!option.exclusive && !!option.contentFields && (
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() =>
                        setDrawerState({
                          open: true,
                          practice: { fields: option.contentFields as PracticeDetailFields },
                          selectInteraction: {
                            onClickSelect: ({ label, value }) =>
                              handleSaveTraitDetail({
                                label,
                                value,
                              }),
                            currentPracticeIsSelected: selectedTileValues.includes(
                              drawerState.practice?.fields.practiceId || ''
                            ),
                          },
                        })
                      }
                    >
                      {t('learnMore')}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid2>
        )
      })}
    </Grid2>
  )
}
